import type { FoodContainer, IsoDate } from './model';

export type PriorityLevel =
  | 'incident'
  | 'overdue'
  | 'today'
  | 'urgent'
  | 'soon'
  | 'normal'
  | 'none';

export interface PriorityResult {
  level: PriorityLevel;
  daysFromHorizon: number | null;
  label: string;
}

const toUtcDay = (date: IsoDate): number => Date.parse(`${date}T12:00:00.000Z`);

const priorityDate = (
  container: FoodContainer,
): { value: IsoDate; label: string } | null => {
  if (container.trackingMode === 'internal-only') {
    const references: Array<{ value: IsoDate; label: string; rank: number }> = [];
    if (container.manufacturerDate != null && container.manufacturerDateType !== 'none') {
      references.push({
        value: container.manufacturerDate,
        label: `${container.manufacturerDateType} fabricant`,
        rank: container.manufacturerDateType === 'DLC' ? 0 : 1,
      });
    }
    if (container.useFirstOn != null) {
      references.push({ value: container.useFirstOn, label: 'Repère choisi', rank: 2 });
    }
    const first = references.sort(
      (left, right) => left.value.localeCompare(right.value) || left.rank - right.rank,
    )[0];
    return first == null ? null : { value: first.value, label: first.label };
  }
  return container.qualityHorizonOn == null
    ? null
    : { value: container.qualityHorizonOn, label: 'Date optimale d’utilisation' };
};

export const calculatePriority = (
  container: FoodContainer,
  today: IsoDate,
): PriorityResult => {
  if (container.incidentStatus === 'unresolved') {
    return {
      level: 'incident',
      daysFromHorizon: null,
      label: 'Incident déclaré — vérifier les conditions',
    };
  }
  const reference = priorityDate(container);
  if (!['present', 'partial', 'draft'].includes(container.status) || reference == null) {
    return {
      level: 'none',
      daysFromHorizon: null,
      label:
        container.trackingMode === 'internal-only'
          ? 'Aucun repère de rotation renseigné'
          : 'Aucune date optimale d’utilisation',
    };
  }
  const days = Math.round((toUtcDay(reference.value) - toUtcDay(today)) / 86_400_000);
  if (days < 0) {
    return {
      level: 'overdue',
      daysFromHorizon: days,
      label: `${reference.label} : date passée de ${Math.abs(days)} j — vérifier`,
    };
  }
  if (days === 0) {
    return {
      level: 'today',
      daysFromHorizon: days,
      label: `${reference.label} aujourd’hui`,
    };
  }
  if (days <= 2) {
    return {
      level: 'urgent',
      daysFromHorizon: days,
      label: `À utiliser en priorité — J-${days}`,
    };
  }
  if (days <= 7) {
    return {
      level: 'soon',
      daysFromHorizon: days,
      label: `Rotation à prévoir — J-${days}`,
    };
  }
  return {
    level: 'normal',
    daysFromHorizon: days,
    label: `${reference.label} dans ${days} j`,
  };
};

export const sortByPriority = (
  containers: FoodContainer[],
  today: IsoDate,
): FoodContainer[] =>
  [...containers].sort((left, right) => {
    const leftPriority = calculatePriority(left, today);
    const rightPriority = calculatePriority(right, today);
    const rank: Record<PriorityLevel, number> = {
      incident: 0,
      overdue: 1,
      today: 2,
      urgent: 3,
      soon: 4,
      normal: 5,
      none: 6,
    };
    const byLevel = rank[leftPriority.level] - rank[rightPriority.level];
    if (byLevel !== 0) {
      return byLevel;
    }
    return (leftPriority.daysFromHorizon ?? Number.MAX_SAFE_INTEGER) -
      (rightPriority.daysFromHorizon ?? Number.MAX_SAFE_INTEGER);
  });
